/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Glass
 * Copyright IBM Corp. 2017
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
/* global define */
define([], function() {
	'use strict'; //NOSONAR: Meant to be strict

	/**
	 * A service for embedding the Medallia NPS widget
	 * @class
	 */

	class NPSService {

		/**
		* initialize the service with properties from glass context, build the metadata object and embed the Medallia tag in the DOM
		* @public
		*/
		initialize(context) {
			this._context = context;
			if (this._context) {
				this._setLogger(context);
				try {
					const ups = this._context.getCoreSvc('.UserProfile');
					this._embedMedalliaTag(ups.NetPromoterScore, this._context.profile.account, ups.preferences);
				} catch (error) {
					this._getLogger().error('NPSService.initialize():: error calling _embedMedalliaTag()', error);
				}
			}
		}

		_embedMedalliaTag(netPromoterScore, account, preferences) {
			if (netPromoterScore && netPromoterScore.isEnabled === true && this._isLoaded() === false && account && preferences) {
				const protoMetadata = {
					country: this._getCountry(preferences.contentLocale),
					language: this._getLanguage(preferences.productLocale),
					noQuarantine: this._getQuarantineStatus(netPromoterScore.noQuarantine),
					daysSinceFirstLogin: this._calculateDaysSinceFirstLogin(account.creationTime),
					givenName: account.givenName,
					surname: account.surname,
					email: account.email,
					id: account.id
				};

				const metadataObj = this._buildMetadataObject(netPromoterScore, protoMetadata);

				if (metadataObj) {
					this._setMedalliaObj(metadataObj);
					this._injectScriptTag();
				}
			}
		}

		_buildMetadataObject(netPromoterScore, protoMetadata) {
			if (netPromoterScore &&
				protoMetadata &&
				protoMetadata.country &&
				protoMetadata.language &&
				protoMetadata.noQuarantine &&
				protoMetadata.daysSinceFirstLogin &&
				(protoMetadata.givenName || protoMetadata.surname) &&
				protoMetadata.email &&
				protoMetadata.id
			) {
				return {
					offeringName: netPromoterScore.offeringName,
					offeringId: netPromoterScore.offeringId,
					highLevelOfferingName: netPromoterScore.highLevelOfferingName,
					userFirstName: protoMetadata.givenName,
					userLastName: protoMetadata.surname,
					userEmail: protoMetadata.email,
					userId: protoMetadata.id,
					userIdType: netPromoterScore.userIdType,
					country: protoMetadata.country,
					language: protoMetadata.language,
					customerName: netPromoterScore.customerName,
					icn: netPromoterScore.icn,
					quarterlyIntercept: netPromoterScore.quarterlyIntercept,
					daysSinceFirstLogin: protoMetadata.daysSinceFirstLogin,
					testData: netPromoterScore.isTestData,
					noQuarantine: protoMetadata.noQuarantine
				};
			} else {
				this._getLogger().error('NPSService::_buildMetadataObject():: Bad input');
				return null;
			}
		}

		_setMedalliaObj(metadata) {
			window.IBM_Meta = metadata;
		}

		_isLoaded() {
			return document.getElementById('medalliaScriptTag') !== null;
		}

		_injectScriptTag() {
			const js = document.createElement('script');
			js.src = 'https://nebula-cdn.kampyle.com/we/28600/onsite/embed.js';
			js.id = 'medalliaScriptTag';
			document.body.appendChild(js);
		}

		_getLanguage(productLocale) {
			let language = '';
			if (productLocale) {
				switch (productLocale.toLowerCase()) {
					case 'zh-tw':
					case 'fr-ca':
					case 'pt-pt':
					case 'es-co':
						language = productLocale;
						break;
					default:
						language = this._splitLocale(productLocale, 'language');
				}
				return language.toLowerCase();
			} else {
				this._getLogger().error('NPSService::_getLanguage():: Bad productLocale');
				return null;
			}
		}

		_getCountry(contentLocale) {
			if (contentLocale) {
				return this._splitLocale(contentLocale, 'country').toLowerCase();
			} else {
				this._getLogger().error('NPSService::_getCountry():: Bad contentLocale');
				return null;
			}
		}

		_splitLocale(locale, option) {
			const splitLocale = locale.split('-');
			if (splitLocale.length === 2) {
				switch (option) {
					case 'language':
						return splitLocale[0];
					case 'country':
						return splitLocale[1];
				}
			} else {
				return locale;
			}
		}

		_calculateDaysSinceFirstLogin(creationTimestamp) {
			const milliSecsInaDay = 1000 * 60 * 60 * 24;
			const creationDate = this._getCreationDate(creationTimestamp);
			const todaysDate = this._getCurrentDate();
			let daysSinceFirstLogin = null;

			if (creationDate && todaysDate) {
				const timeDifference = todaysDate.getTime() - creationDate.getTime();
				daysSinceFirstLogin = Math.round(timeDifference / milliSecsInaDay);
				if (daysSinceFirstLogin < 1) {
					this._getLogger().error('NPSService::_calculateDaysSinceFirstLogin():: daysSinceFirstLogin is < 1');
				}
			} else {
				this._getLogger().error('NPSService::_calculateDaysSinceFirstLogin():: Passed bad timestamp');
			}

			return daysSinceFirstLogin;
		}

		_getCurrentDate() {
			return new Date();
		}

		_getCreationDate(timestamp) {
			let creationDate;
			if (timestamp) {
				creationDate = new Date(timestamp);
				if (isNaN(creationDate)) {
					creationDate = null;
				}
			}
			return creationDate;
		}

		_getQuarantineStatus(noQuarantine) {
			if (noQuarantine === true) {
				return 'yes';
			} else {
				return 'no';
			}
		}

		_setLogger(context) {
			if (context.glassContext && context.glassContext.getCoreSvc('.Logger')) {
				this._logger = context.glassContext.getCoreSvc('.Logger');
			} else {
				this._logger = console;
			}
		}

		_getLogger() {
			return this._logger;
		}
	}

	return NPSService;
});
